import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../mockData';

const ContactPage = () => {
  // --- LOGIC START: FORMSPREE SETUP ---
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error

  // 🔴 PASTE YOUR FORMSPREE ID HERE
  const FORMSPREE_ENDPOINT = "https://formspree.io/f/xeeeqpye"; 

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    const formData = new FormData(e.target);
    
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset(); // Clear the form
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };
  // --- LOGIC END ---

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1600&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-white">
            <span className="inline-block bg-[#4CAF50] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Get In Touch
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-green-100 leading-relaxed">
              Get in touch for a free consultation and quote
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* LEFT SIDE: Contact Form */}
            <div>
              <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">
                Send Us a Message
              </h2>

              {formStatus === 'success' ? (
                // SUCCESS MESSAGE (Shows after sending)
                <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center animate-fade-in">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#1E5631]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E5631] mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">Thank you for reaching out. We will get back to you shortly.</p>
                  <button 
                    onClick={() => setFormStatus('idle')}
                    className="text-[#4CAF50] font-semibold hover:text-[#1E5631]"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                // THE FORM
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                    <input type="text" name="name" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all" placeholder="John Doe" />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <input type="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all" placeholder="john@example.com" />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <input type="tel" name="phone" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all" placeholder="+91 98765 43210" />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">Service Required *</label>
                    <select name="service" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all">
                      <option value="">Select a service</option>
                      <option value="Landscape Design">Landscape Design & Planning</option>
                      <option value="Garden Construction">Garden Construction & Installation</option>
                      <option value="Horticulture">Horticulture & Planting</option>
                      <option value="Lawn Management">Lawn & Turf Management</option>
                      <option value="Irrigation">Irrigation Systems Setup</option>
                      <option value="Maintenance">Maintenance & Seasonal Care</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea name="message" rows="5" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all resize-none" placeholder="Tell us about your project..."></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full btn-primary bg-[#4CAF50] text-white py-4 rounded-lg font-bold flex items-center justify-center hover:bg-[#45a049] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                    {!formStatus === 'submitting' && <Send className="ml-2 w-5 h-5" />}
                  </button>
                  
                  {formStatus === 'error' && (
                    <p className="text-red-500 text-center text-sm">Failed to send. Please check your internet connection.</p>
                  )}
                </form>
              )}
            </div>

            {/* RIGHT SIDE: Info & Map */}
            <div>
              <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">
                Get in Touch
              </h2>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] rounded-full p-3 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Our Location</h3>
                    <p className="text-gray-700">{companyInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] rounded-full p-3 flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                    <a href={`tel:${companyInfo.phone}`} className="text-gray-700 hover:text-[#1E5631] transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] rounded-full p-3 flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                    <a href={`mailto:${companyInfo.email}`} className="text-gray-700 hover:text-[#1E5631] transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-12 bg-gradient-to-br from-[#1E5631] to-[#2d7a47] rounded-xl p-8 text-white">
                <h3 className="font-heading text-2xl font-semibold mb-4">Business Hours</h3>
                <div className="space-y-2 text-green-100">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span className="font-medium">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span className="font-medium">9:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-12">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">Find Us</h3>
                <div className="bg-gray-100 rounded-xl h-80 overflow-hidden shadow-md">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55574.89267890279!2d78.18234!3d28.79334!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390afabddf75f4d1%3A0x7c24a14eb59e5dd2!2sGajraula%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Udyaan Landscaping Location"
                  ></iframe>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#1E5631] to-[#2d7a47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1600&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Visit Our Office
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Schedule an in-person consultation at our office in Gajraula
          </p>
          <a
            href={`tel:${companyInfo.phone}`}
            className="inline-block bg-[#4CAF50] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#45a049] transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Call to Schedule
          </a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;